import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import FullCalendar from "@fullcalendar/react";
import listPlugin from "@fullcalendar/list";
import luxonPlugin from "@fullcalendar/luxon3";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { RootState } from "typedefs";
import { CalendarPreferences } from "app/models/CalendarPreferences";
import {
  setSelectedEvent,
  fetchSchedule,
  fetchMoreSchedule,
  pollScheduleSegments,
  clearEvents,
} from "app/actions/events";
import useInterval from "app/utils/hooks/useInterval";
import i18n from "app/i18n";

import ScheduleEvent from "../../Event/ScheduleEvent";
import styles from "./styles.module.scss";

const SCHEDULE_LIMIT = 50;

interface ScheduleViewProps {
  calendarRef: React.RefObject<any>;
  timeZone: string;
  preferences: CalendarPreferences;
  eventElRef: React.RefObject<any>;
  selectedDate: DateTime;
  setCurrentDay: (day: DateTime) => void;
  onDateChangeRef?: React.MutableRefObject<((date: DateTime) => void) | null>;
}

export default function ScheduleView({
  calendarRef,
  timeZone,
  preferences,
  eventElRef,
  selectedDate,
  setCurrentDay,
  onDateChangeRef,
}: ScheduleViewProps) {
  const dispatch = useDispatch();
  const eventsState = useSelector((state: RootState) => state.events);
  const events = eventsState.data as any[];
  const isLoading = eventsState.pending;
  const { t } = useTranslation();

  const eventsRef = useRef(events);
  eventsRef.current = events;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollCleanupRef = useRef<(() => void) | null>(null);
  const currentDateRef = useRef<string>(null);
  const pendingNavTargetRef = useRef<string>(null);
  const prefetchedUpToRef = useRef<string>(null);
  const exhaustedRef = useRef(false);
  const pollStartRef = useRef<string>(null);
  const pollEndRef = useRef<string>(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const today = useMemo(() => DateTime.now().startOf("day"), []);

  const calendarEvents = useMemo(() => {
    if (!events || events.length === 0) {
      return [];
    }

    return events.map((event: any) => ({
      ...event,
      id: `${event.id}_${event.start}`,
    }));
  }, [events]);

  const visibleRange = useMemo(() => {
    if (!events || events.length === 0) {
      const start = selectedDate || today;
      return {
        start: start.toJSDate(),
        end: start.plus({ days: 1 }).toJSDate(),
      };
    }
    const firstDate = DateTime.fromISO(events[0].start).startOf("day");
    const lastDate = DateTime.fromISO(events[events.length - 1].start)
      .startOf("day")
      .plus({ days: 1 });
    return {
      start: firstDate.toJSDate(),
      end: lastDate.toJSDate(),
    };
  }, [events, selectedDate, today]);

  const renderEvent = useCallback(
    (info: any) => {
      return <ScheduleEvent {...info} timeZone={timeZone} />;
    },
    [timeZone],
  );

  const handleEventClick = useCallback(
    (info: any) => {
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        (eventElRef as React.MutableRefObject<any>).current = info.el;
      } else {
        const { clientY } = info.jsEvent;
        const elRect = info.el.getBoundingClientRect();
        const anchorX = elRect.left + 450;
        (eventElRef as React.MutableRefObject<any>).current = {
          getBoundingClientRect: () => ({
            width: 0,
            height: 0,
            x: anchorX,
            y: clientY,
            top: clientY,
            left: anchorX,
            right: anchorX,
            bottom: clientY,
          }),
        };
      }

      const compositeId = info.event.id as string;
      const separatorIdx = compositeId.indexOf("_");
      const originalId = parseInt(compositeId.substring(0, separatorIdx), 10);
      const originalStart = compositeId.substring(separatorIdx + 1);
      const exactMatch = eventsRef.current?.find(
        (e: any) => e.id === originalId && e.start === originalStart,
      );
      if (exactMatch) {
        dispatch(setSelectedEvent(exactMatch.id));
      } else {
        dispatch(setSelectedEvent(originalId));
      }
    },
    [dispatch, eventElRef],
  );

  const highlightDayLabel = useCallback((date: DateTime) => {
    if (scrollCleanupRef.current) {
      scrollCleanupRef.current();
      scrollCleanupRef.current = null;
    }

    const dateStr = date.toISODate();
    let dayRow = document.querySelector(
      `.fc-list-day[data-date="${dateStr}"]`,
    ) as HTMLElement;

    if (!dayRow) {
      const allDayRows = Array.from(
        document.querySelectorAll(".fc-list-day[data-date]"),
      ) as HTMLElement[];
      dayRow =
        allDayRows.find((row) => row.getAttribute("data-date") >= dateStr) ||
        allDayRows[allDayRows.length - 1];
    }

    if (!dayRow) {
      return;
    }

    const cushion = dayRow.querySelector(".fc-list-day-cushion") as HTMLElement;
    if (!cushion) {
      return;
    }

    cushion.classList.remove("schedule-day-highlight");
    // tslint:disable-next-line:no-unused-expression
    void cushion.offsetWidth; // Force DOM reflow
    cushion.classList.add("schedule-day-highlight");

    const timer = window.setTimeout(() => {
      cushion.classList.remove("schedule-day-highlight");
    }, 1500);

    scrollCleanupRef.current = () => {
      clearTimeout(timer);
      cushion.classList.remove("schedule-day-highlight");
    };

    const container = scrollContainerRef.current;
    if (container) {
      const containerRect = container.getBoundingClientRect();
      const rowRect = dayRow.getBoundingClientRect();
      const targetTop = container.scrollTop + rowRect.top - containerRect.top;

      if (Math.abs(container.scrollTop - targetTop) > 1) {
        container.scrollTo({ top: targetTop, behavior: "smooth" });
      }
    }
  }, []);

  const prefetchMore = useCallback(() => {
    if (!events || events.length === 0) {
      return;
    }
    if (exhaustedRef.current) {
      return;
    }
    const lastDate = events[events.length - 1].start.slice(0, 10);

    if (prefetchedUpToRef.current && prefetchedUpToRef.current >= lastDate) {
      return;
    }

    prefetchedUpToRef.current = lastDate;
    const afterDate = DateTime.fromISO(lastDate).plus({ days: 1 }).toISODate();
    (
      dispatch(
        fetchMoreSchedule({ after: afterDate, limit: SCHEDULE_LIMIT }) as any,
      ) as Promise<number>
    )?.then((count: number) => {
      if (count === 0) {
        exhaustedRef.current = true;
        if (pendingNavTargetRef.current) {
          pendingNavTargetRef.current = null;
          currentDateRef.current = lastDate;
          const dt = DateTime.fromISO(lastDate);
          setCurrentDay(dt);
          requestAnimationFrame(() => highlightDayLabel(dt));
        }
      }
    });
  }, [dispatch, events, setCurrentDay, highlightDayLabel]);

  // Initial fetch
  useEffect(() => {
    dispatch(clearEvents());
    const afterIso = today.toISODate();
    currentDateRef.current = afterIso;
    pendingNavTargetRef.current = afterIso;
    pollStartRef.current = afterIso;
    dispatch(fetchSchedule({ after: afterIso, limit: SCHEDULE_LIMIT }) as any);
  }, []);

  useEffect(() => {
    if (!initialLoadDone) {
      return;
    }
    if (!selectedDate) {
      return;
    }
    const targetIso = selectedDate.toISODate();
    if (targetIso === currentDateRef.current) {
      return;
    }

    const currentIso = currentDateRef.current;
    const isForward = targetIso > currentIso;

    if (events?.length > 0) {
      let batchDate: string | undefined;

      if (isForward) {
        batchDate = events
          .find((e: any) => e.start.slice(0, 10) >= targetIso)
          ?.start.slice(0, 10);
      } else {
        for (let i = events.length - 1; i >= 0; i--) {
          const d = events[i].start.slice(0, 10);
          if (d <= targetIso) {
            batchDate = d;
            break;
          }
        }
      }

      if (batchDate) {
        currentDateRef.current = batchDate;
        const dt = DateTime.fromISO(batchDate);
        if (batchDate !== targetIso) {
          setCurrentDay(dt);
        }
        requestAnimationFrame(() => highlightDayLabel(dt));

        if (
          isForward &&
          batchDate === events[events.length - 1].start.slice(0, 10)
        ) {
          prefetchMore();
        }
        return;
      }
    }

    if (isForward && events?.length > 0) {
      const lastDate = events[events.length - 1].start.slice(0, 10);

      if (exhaustedRef.current) {
        currentDateRef.current = lastDate;
        const dt = DateTime.fromISO(lastDate);
        if (lastDate !== selectedDate.toISODate()) {
          setCurrentDay(dt);
        }
        requestAnimationFrame(() => highlightDayLabel(dt));
        return;
      }

      currentDateRef.current = lastDate;
      pendingNavTargetRef.current = targetIso;
      prefetchMore();
    } else {
      currentDateRef.current = targetIso;
      pendingNavTargetRef.current = targetIso;
      prefetchedUpToRef.current = null;
      exhaustedRef.current = false;
      pollStartRef.current = targetIso;
      pollEndRef.current = null;
      dispatch(
        fetchSchedule({ after: targetIso, limit: SCHEDULE_LIMIT }) as any,
      );
    }
  }, [
    initialLoadDone,
    selectedDate?.toMillis(),
    dispatch,
    events,
    setCurrentDay,
    highlightDayLabel,
    prefetchMore,
  ]);

  useEffect(() => {
    if (!pendingNavTargetRef.current) {
      return;
    }

    if (isLoading) {
      return;
    }

    if (!events || events.length === 0) {
      pendingNavTargetRef.current = null;

      if (!initialLoadDone) {
        setInitialLoadDone(true);
      }

      return;
    }

    const targetIso = pendingNavTargetRef.current;

    const matchDate = events
      .find((e: any) => e.start.slice(0, 10) >= targetIso)
      ?.start.slice(0, 10);

    if (!matchDate) {
      return;
    }

    pendingNavTargetRef.current = null;

    if (!initialLoadDone) {
      setInitialLoadDone(true);
    }

    const dt = DateTime.fromISO(matchDate);

    if (!selectedDate || matchDate !== selectedDate.toISODate()) {
      currentDateRef.current = matchDate;
      setCurrentDay(dt);
    }

    const tryHighlight = (attempts: number) => {
      const dayRow = document.querySelector(
        `.fc-list-day[data-date="${matchDate}"]`,
      );
      if (dayRow || attempts >= 10) {
        highlightDayLabel(dt);
      } else {
        requestAnimationFrame(() => tryHighlight(attempts + 1));
      }
    };

    requestAnimationFrame(() => tryHighlight(0));
  }, [
    events,
    isLoading,
    selectedDate,
    setCurrentDay,
    highlightDayLabel,
    initialLoadDone,
  ]);

  const handleCalendarDateChange = useCallback(
    (date: DateTime) => {
      const clampedDate = date < today ? today : date;
      setCurrentDay(clampedDate);
    },
    [today, setCurrentDay],
  );

  useEffect(() => {
    if (onDateChangeRef) {
      onDateChangeRef.current = handleCalendarDateChange;
    }
    return () => {
      if (onDateChangeRef) {
        onDateChangeRef.current = null;
      }
    };
  }, [onDateChangeRef, handleCalendarDateChange]);

  useEffect(() => {
    if (!events || events.length === 0) {
      return;
    }

    if (!pollStartRef.current) {
      return;
    }

    pollEndRef.current = DateTime.fromISO(
      events[events.length - 1].start.slice(0, 10),
    )
      .plus({ days: 1 })
      .toISODate();
  }, [events]);

  useInterval(() => {
    if (!pollStartRef.current || isLoading) {
      return;
    }

    const currentEvents = eventsRef.current;

    if (!currentEvents || currentEvents.length === 0 || !pollEndRef.current) {
      return;
    }

    const segments: Array<{ after: string; limit: number; mergeEnd: string }> =
      [];

    for (let i = 0; i < currentEvents.length; i += SCHEDULE_LIMIT) {
      const after =
        i === 0 ? pollStartRef.current : currentEvents[i].start.slice(0, 10);
      const mergeEnd =
        i + SCHEDULE_LIMIT >= currentEvents.length
          ? pollEndRef.current
          : currentEvents[i + SCHEDULE_LIMIT].start.slice(0, 10);
      segments.push({ after, limit: SCHEDULE_LIMIT, mergeEnd });
    }

    dispatch(pollScheduleSegments(segments) as any);
  }, 60 * 1000);

  const showSpinner = !initialLoadDone || (initialLoadDone && isLoading && (!events || events.length === 0));
  const showCalendar = initialLoadDone && !showSpinner;

  return (
    <div className={styles.schedule_events} ref={scrollContainerRef}>
      {showCalendar && (
        <FullCalendar
          headerToolbar={false}
          ref={calendarRef}
          events={calendarEvents}
          locale={i18n.language}
          plugins={[listPlugin, luxonPlugin]}
          initialView="scheduleList"
          initialDate={today.toJSDate()}
          timeZone={timeZone}
          height="auto"
          firstDay={preferences.firstDay}
          views={{
            scheduleList: {
              type: "list",
              visibleRange: () => visibleRange,
              listDayFormat:
                preferences.dateFormat === "eu"
                  ? "EEEE ' - ' d MMMM"
                  : "EEEE ' - ' MMMM d",
              listDaySideFormat: false as const,
              noEventsContent: t("No upcoming sessions"),
            },
          }}
          eventTimeFormat={{
            hour: "numeric" as const,
            minute: "2-digit" as const,
            omitZeroMinute: true,
            hour12: preferences.timeFormat === "12h",
          }}
          eventContent={renderEvent}
          eventClick={handleEventClick}
        />
      )}
      {showSpinner && (
        <div className={styles.schedule_loading}>
          <div className={styles.schedule_spinner} />
        </div>
      )}
    </div>
  );
}
